import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface UseSwipeNavigationOptions {
  /** Minimum horizontal distance in px to trigger navigation */
  threshold?: number;
  /** Maximum angle from horizontal in degrees */
  maxAngleDeg?: number;
  /** Maximum touch duration in ms */
  maxDurationMs?: number;
  /** Whether swipe navigation is enabled */
  enabled?: boolean;
}

/**
 * Detects horizontal touch swipes on the attached element and navigates
 * forward/back via react-router. Swipe right → back, swipe left → forward.
 *
 * Only activates on touch devices.
 */
export function useSwipeNavigation<T extends HTMLElement = HTMLElement>(
  options: UseSwipeNavigationOptions = {}
) {
  const {
    threshold = 80,
    maxAngleDeg = 30,
    maxDurationMs = 500,
    enabled = true,
  } = options;

  const navigate = useNavigate();
  const ref = useRef<T | null>(null);
  const touchStart = useRef<{ x: number; y: number; time: number } | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    const maxAngleRad = (maxAngleDeg * Math.PI) / 180;

    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      touchStart.current = { x: t.clientX, y: t.clientY, time: Date.now() };
    };

    const onTouchEnd = (e: TouchEvent) => {
      const start = touchStart.current;
      if (!start) return;

      const t = e.changedTouches[0];
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      const elapsed = Date.now() - start.time;
      touchStart.current = null;

      if (Math.abs(dx) < threshold || elapsed > maxDurationMs) return;
      // Must be more horizontal than vertical (within angle tolerance)
      if (Math.abs(dy) > Math.abs(dx) * Math.tan(maxAngleRad)) return;

      navigate(dx > 0 ? -1 : 1);
    };

    const onTouchCancel = () => {
      touchStart.current = null;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchCancel, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [navigate, threshold, maxAngleDeg, maxDurationMs, enabled]);

  return ref;
}
